/* CUSTOM — accordion behaviour, shared by the product buy box and the FAQ.

   Both are native <details>/<summary>, which is why they work with JavaScript
   off and why they are worth keeping. What the element does not give us is
   either of the two things wanted here: only one row open at a time, and a
   height that moves rather than jumps.

   Animating a <details> means taking the toggle over. The browser hides the
   body the instant `open` is removed, so a closing row has to stay open for as
   long as the animation runs and be closed at the end of it; an opening row is
   opened first, so its natural height can be measured, and animated up to it.

   Groups are siblings: the rows that share a parent close each other. The buy
   box's three sit next to each other in .product__info-container, the FAQ's in
   .mg-faq__list, and neither knows about the other. */

(() => {
  /* Only ours. Dawn's own <details> — the footer, the drawers, the variant
     dropdowns — keep the browser's behaviour. */
  const GROUPS = ['mg-accordion', 'mg-faq__item'];
  const DURATION = 260;
  const running = new WeakMap();

  /* No animation when the reader has asked for none — and none when the page is
     hidden, because a hidden document does not advance animations at all: the
     `finished` promise never settles, and a close that waits on it would leave
     the row open for good. */
  const shouldAnimate = () =>
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !document.hidden;

  const groupClass = (details) => GROUPS.find((name) => details.classList.contains(name));

  /* Whatever the row's content element happens to be called — the two use
     different class names for the same job. */
  const bodyOf = (details) => Array.from(details.children).find((el) => el.tagName !== 'SUMMARY');

  const cancel = (details) => {
    const current = running.get(details);
    if (!current) return;
    running.delete(details);
    current.cancel();
  };

  /* Height alone is not enough. Dawn sets box-sizing: border-box globally, so a
     body animated to `height: 0` still paints its own 18–20px of padding — the
     row snapped by that much at each end. The padding has to travel with it.

     fill: 'forwards' for the same reason at the other end: without it the
     natural height paints for one frame between the animation finishing and
     `open` being dropped, which is the flash at the close. The animation is
     cancelled once the row is actually closed, so nothing is left applied. */
  const animate = (details, body, opening) => {
    const style = getComputedStyle(body);
    const shut = { height: '0px', paddingTop: '0px', paddingBottom: '0px' };
    const open = {
      height: `${body.getBoundingClientRect().height}px`,
      paddingTop: style.paddingTop,
      paddingBottom: style.paddingBottom,
    };

    /* The fade is offset rather than spread across the whole move: text
       arriving at full opacity while the box is still growing reads as one
       gesture, where a fade running the entire length reads as two. */
    const frames = opening
      ? [
          { ...shut, opacity: 0, offset: 0 },
          { opacity: 1, offset: 0.6 },
          { ...open, opacity: 1, offset: 1 },
        ]
      : [
          { ...open, opacity: 1, offset: 0 },
          { opacity: 0, offset: 0.5 },
          { ...shut, opacity: 0, offset: 1 },
        ];

    body.style.overflow = 'hidden';
    const animation = body.animate(frames, {
      duration: DURATION,
      easing: 'cubic-bezier(0.33, 0, 0.2, 1)',
      fill: 'forwards',
    });
    running.set(details, animation);

    /* `finished` rejects when a cancel interrupts it, which is a normal way for
       this to end — the row was clicked again mid-animation. */
    return animation.finished
      .then(() => {
        running.delete(details);
        if (!opening) details.open = false;
        animation.cancel();
        body.style.overflow = '';
      })
      .catch(() => {});
  };

  const expand = (details) => {
    const body = bodyOf(details);
    if (!body) return;

    cancel(details);
    details.open = true;
    if (!shouldAnimate()) return;

    /* Measured after opening and after any interrupted animation is cancelled,
       so this is the row's real height rather than one frame of a transition. */
    animate(details, body, true);
  };

  const collapse = (details) => {
    const body = bodyOf(details);
    if (!body || !details.open) return;

    cancel(details);
    if (!shouldAnimate()) {
      details.open = false;
      return;
    }

    animate(details, body, false);
  };

  const siblings = (details, name) =>
    Array.from(details.parentElement.children).filter(
      (el) => el !== details && el.tagName === 'DETAILS' && el.classList.contains(name)
    );

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;

    const name = groupClass(details);
    if (!name) return;

    /* The browser would toggle `open` immediately, which is the jump we are
       replacing. Enter and Space on a focused summary arrive here as clicks
       too, so the keyboard is covered by the same handler. */
    event.preventDefault();

    if (details.open) {
      collapse(details);
      return;
    }

    siblings(details, name).forEach(collapse);
    expand(details);
  });
})();
